// src/components/ExportModal.jsx - Version corrigée
import React, { useState } from 'react';
import { X, FileSpreadsheet, Share2, Settings, CheckCircle, Link as LinkIcon, Download } from 'lucide-react';

const ExportModal = ({ isOpen, onClose, candidates }) => {
  const [exportOptions, setExportOptions] = useState({
    includePersonalInfo: true,
    includeScores: true,
    includeRecommendations: true,
    includeAnalysis: true,
    shareWithTeam: false
  });

  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  if (!isOpen) return null;

  const generateCSVContent = () => {
    const headers = [];
    
    if (exportOptions.includePersonalInfo) {
      headers.push('Prénom', 'Nom', 'Email', 'Téléphone');
    }
    if (exportOptions.includeScores) {
      headers.push('Score', 'Statut', 'Classement');
    }
    if (exportOptions.includeRecommendations) {
      headers.push('Recommandation IA', 'Justification');
    }
    if (exportOptions.includeAnalysis) {
      headers.push('Expérience (ans)', 'Diplôme', 'Entreprises', 'Compétences');
    }

    const csvRows = [headers.join(',')];

    candidates.forEach((candidate, index) => {
      const row = [];
      
      if (exportOptions.includePersonalInfo) {
        row.push(
          `"${candidate.prenom || ''}"`,
          `"${candidate.nom || ''}"`,
          `"${candidate.email || ''}"`,
          `"${candidate.telephone || ''}"`
        );
      }
      if (exportOptions.includeScores) {
        row.push(
          candidate.score || 0,
          `"${candidate.statut || ''}"`,
          index + 1
        );
      }
      if (exportOptions.includeRecommendations) {
        row.push(
          `"${candidate.recommandation_finale || ''}"`,
          `"${candidate.justification_recommandation || ''}"`
        );
      }
      if (exportOptions.includeAnalysis) {
        row.push(
          candidate.experience_totale || 0,
          `"${candidate.diplome_plus_haut || ''}"`,
          `"${candidate.entreprises?.join('; ') || ''}"`,
          `"${candidate.competences_techniques?.join('; ') || ''}"`
        );
      }
      
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  };

  const handleExportToCSV = async () => {
    setIsExporting(true);
    
    try {
      // Générer le contenu CSV
      const csvContent = generateCSVContent();
      
      // Créer un Blob avec le contenu CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Créer l'URL de téléchargement
      const url = URL.createObjectURL(blob);
      
      // Créer le lien de téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.download = `screening-candidats-${new Date().toISOString().split('T')[0]}.csv`;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Nettoyer l'URL
      URL.revokeObjectURL(url);
      
      setExportResult({
        success: true,
        fileName: `screening-candidats-${new Date().toISOString().split('T')[0]}.csv`,
        message: 'Fichier CSV téléchargé avec succès'
      });
      
    } catch (error) {
      console.error('Erreur export:', error);
      setExportResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportToSheets = () => {
    // Ouvrir Google Sheets avec instructions
    const instructionsUrl = 'https://docs.google.com/spreadsheets/create';
    window.open(instructionsUrl, '_blank');
    
    setExportResult({
      success: true,
      fileName: 'Instructions ouvertes',
      message: 'Google Sheets ouvert. Importez le fichier CSV téléchargé via Fichier > Importer.'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl mx-4 bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl">
        
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">📊 Export vers Google Sheets</h3>
              <p className="text-gray-400 text-sm">Export CSV de {candidates.length} candidats pour Google Sheets</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {!exportResult ? (
            <div className="space-y-6">
              <div>
                <h4 className="text-white font-semibold mb-4">Données à exporter</h4>
                <div className="space-y-3">
                  {[
                    { key: 'includePersonalInfo', label: 'Informations personnelles (nom, email, téléphone)', icon: '👤' },
                    { key: 'includeScores', label: 'Scores et classements IA', icon: '📈' },
                    { key: 'includeRecommendations', label: 'Recommandations Claude IA', icon: '🤖' },
                    { key: 'includeAnalysis', label: 'Analyses détaillées et critères', icon: '📋' }
                  ].map((option) => (
                    <label key={option.key} className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all cursor-pointer">
                      <input
                        type="checkbox"
                        checked={exportOptions[option.key]}
                        onChange={(e) => setExportOptions(prev => ({
                          ...prev,
                          [option.key]: e.target.checked
                        }))}
                        className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-lg">{option.icon}</span>
                      <span className="text-white">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {isExporting && (
                <div className="p-6 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                  <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-green-300 font-medium">Génération du fichier CSV...</p>
                  <p className="text-green-400 text-sm mt-1">Préparation des données pour Google Sheets</p>
                </div>
              )}

              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Settings className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <h6 className="text-blue-300 font-medium mb-1">📋 Processus d'export</h6>
                    <ol className="text-blue-200 text-sm space-y-1">
                      <li>1. Téléchargement du fichier CSV</li>
                      <li>2. Ouverture de Google Sheets</li>
                      <li>3. Importation du fichier via Fichier . Importer</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          ) : exportResult.success ? (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-white mb-2">✅ Export réussi !</h4>
                <p className="text-gray-400">{exportResult.message}</p>
                <p className="text-gray-500 text-sm mt-2">Fichier: {exportResult.fileName}</p>
              </div>
              <button
                onClick={handleImportToSheets}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg text-white font-medium transition-all mx-auto"
              >
                <FileSpreadsheet className="w-5 h-5" />
                <span>Ouvrir Google Sheets</span>
              </button>
            </div>
          ) : (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <X className="w-12 h-12 text-red-400" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-white mb-2">❌ Erreur d'export</h4>
                <p className="text-gray-400">{exportResult.error}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <div className="text-gray-400 text-sm">
            {!exportResult ? 'Format CSV compatible Google Sheets' : ''}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white transition-all"
            >
              {exportResult ? 'Fermer' : 'Annuler'}
            </button>
            {!exportResult && (
              <button
                onClick={handleExportToCSV}
                disabled={isExporting}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-all"
              >
                <Download className="w-4 h-4" />
                <span>{isExporting ? 'Export...' : 'Télécharger CSV'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;